import { supabase } from './supabase';
import type { ApiResponse } from './appConfigService';

export type SecurityEventFamily = 'auth' | 'admin_action' | 'account_recovery' | 'privilege_change';
export type SecurityEventSeverity = 'info' | 'warning' | 'error' | 'critical';
export type SecurityEventOutcome = 'success' | 'failure' | 'blocked';
export type SecurityAlertStatus = 'open' | 'acknowledged' | 'resolved';

export interface SecurityEventUserRef {
  id: string;
  full_name?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface SecurityEventRecord {
  id: string;
  created_at: string;
  event_family: SecurityEventFamily;
  event_type: string;
  severity: SecurityEventSeverity;
  outcome: SecurityEventOutcome;
  source_name: string;
  actor_user_id?: string | null;
  actor_user?: SecurityEventUserRef | null;
  target_user_id?: string | null;
  target_user?: SecurityEventUserRef | null;
  session_id?: string | null;
  ip_hash?: string | null;
  identifier_hash?: string | null;
  identifier_masked?: string | null;
  user_agent?: string | null;
  path?: string | null;
  request_method?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface SecuritySummary {
  failedSignIns24h: number;
  recoveryFailures24h: number;
  privilegedActions24h: number;
  openCriticalAlerts: number;
  lastEventAt?: string | null;
}

export interface SecurityClientEventPayload {
  eventFamily: SecurityEventFamily;
  eventType: string;
  severity?: SecurityEventSeverity;
  outcome: SecurityEventOutcome;
  sourceName: string;
  identifier?: string | null;
  targetUserId?: string | null;
  sessionId?: string | null;
  metadata?: Record<string, unknown> | null;
  path?: string | null;
}

export interface SecurityAlertRecord {
  id: string;
  created_at: string;
  updated_at: string;
  status: SecurityAlertStatus;
  severity: SecurityEventSeverity;
  title: string;
  description?: string | null;
  source: string;
  dedupe_key?: string | null;
  first_seen_at: string;
  last_seen_at: string;
  occurrence_count: number;
  metadata?: Record<string, unknown> | null;
  acknowledged_by?: string | null;
  acknowledged_at?: string | null;
  resolved_by?: string | null;
  resolved_at?: string | null;
}

export interface SecurityAlertRunRecord {
  id: string;
  created_at: string;
  detector_name: string;
  outcome: 'success' | 'error' | 'partial';
  duration_ms?: number | null;
  alerts_created: number;
  alerts_updated: number;
  summary?: Record<string, unknown> | null;
  error?: string | null;
}

export const isSecurityAlertSource = (source?: string | null) => Boolean(source?.startsWith('security.'));

const isoHoursAgo = (hours: number) => new Date(Date.now() - hours * 3_600_000).toISOString();

const currentPagePath = () =>
  typeof window === 'undefined' ? null : `${window.location.pathname}${window.location.search}`;

export const securityErrorToMetadata = (error: unknown): Record<string, unknown> | null => {
  if (!error) return null;
  if (error instanceof Error) {
    const typed = error as Error & { code?: string; status?: number };
    return {
      name: typed.name,
      message: typed.message,
      code: typed.code ?? null,
      status: typed.status ?? null,
    };
  }
  if (typeof error === 'object') {
    const value = error as Record<string, unknown>;
    return {
      message: typeof value.message === 'string' ? value.message : String(error),
      code: typeof value.code === 'string' ? value.code : null,
      status: typeof value.status === 'number' ? value.status : null,
    };
  }
  return { message: String(error) };
};

export const securityMonitorService = {
  async logClientSecurityEvent(payload: SecurityClientEventPayload): Promise<ApiResponse<null>> {
    try {
      const { data, error } = await supabase.functions.invoke('log-security-event', {
        body: {
          event_family: payload.eventFamily,
          event_type: payload.eventType,
          severity: payload.severity ?? 'info',
          outcome: payload.outcome,
          source_name: payload.sourceName,
          identifier: payload.identifier ?? null,
          target_user_id: payload.targetUserId ?? null,
          session_id: payload.sessionId ?? null,
          path: payload.path ?? currentPagePath(),
          metadata: payload.metadata ?? null,
        },
      });
      if (error) {
        return { success: false, message: error.message };
      }
      if (data && typeof data === 'object' && 'error' in data) {
        return { success: false, message: String((data as { error?: unknown }).error ?? 'Failed to log security event.') };
      }
      return { success: true, data: null };
    } catch (error: any) {
      return { success: false, message: error?.message || 'Failed to log security event.' };
    }
  },

  async getSecuritySummary(hours = 24): Promise<ApiResponse<SecuritySummary>> {
    try {
      const cutoff = isoHoursAgo(hours);
      const [
        failedSignInsResult,
        recoveryFailuresResult,
        privilegedActionsResult,
        openCriticalAlertsResult,
        latestEventResult,
      ] = await Promise.all([
        supabase
          .from('security_events')
          .select('id', { count: 'exact', head: true })
          .eq('event_family', 'auth')
          .eq('event_type', 'sign_in')
          .eq('outcome', 'failure')
          .gte('created_at', cutoff),
        supabase
          .from('security_events')
          .select('id', { count: 'exact', head: true })
          .eq('event_family', 'account_recovery')
          .eq('outcome', 'failure')
          .gte('created_at', cutoff),
        supabase
          .from('security_events')
          .select('id', { count: 'exact', head: true })
          .in('event_family', ['admin_action', 'privilege_change'])
          .eq('outcome', 'success')
          .gte('created_at', cutoff),
        supabase
          .from('monitoring_alerts')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'open')
          .eq('severity', 'critical')
          .like('source', 'security.%'),
        supabase
          .from('security_events')
          .select('created_at')
          .order('created_at', { ascending: false })
          .limit(1),
      ]);

      const queryError =
        failedSignInsResult.error ||
        recoveryFailuresResult.error ||
        privilegedActionsResult.error ||
        openCriticalAlertsResult.error ||
        latestEventResult.error;
      if (queryError) {
        return { success: false, message: queryError.message };
      }

      return {
        success: true,
        data: {
          failedSignIns24h: failedSignInsResult.count ?? 0,
          recoveryFailures24h: recoveryFailuresResult.count ?? 0,
          privilegedActions24h: privilegedActionsResult.count ?? 0,
          openCriticalAlerts: openCriticalAlertsResult.count ?? 0,
          lastEventAt: latestEventResult.data?.[0]?.created_at ?? null,
        },
      };
    } catch (error: any) {
      return { success: false, message: error?.message || 'Failed to load security summary.' };
    }
  },

  async getSecurityEventsPage(
    limit = 25,
    offset = 0,
    options?: {
      family?: SecurityEventFamily;
      severity?: SecurityEventSeverity;
      outcome?: SecurityEventOutcome;
      sourceName?: string;
      search?: string;
    }
  ): Promise<ApiResponse<SecurityEventRecord[]> & { count?: number | null }> {
    try {
      let query = supabase
        .from('security_events')
        .select(
          '*, actor_user:users!security_events_actor_user_id_fkey(id, full_name, email, phone), target_user:users!security_events_target_user_id_fkey(id, full_name, email, phone)',
          { count: 'exact' }
        )
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (options?.family) query = query.eq('event_family', options.family);
      if (options?.severity) query = query.eq('severity', options.severity);
      if (options?.outcome) query = query.eq('outcome', options.outcome);
      if (options?.sourceName) query = query.eq('source_name', options.sourceName);
      if (options?.search) {
        query = query.or(
          `event_type.ilike.%${options.search}%,source_name.ilike.%${options.search}%,session_id.ilike.%${options.search}%,identifier_masked.ilike.%${options.search}%`
        );
      }

      const { data, error, count } = await query;
      if (error) {
        return { success: false, message: error.message, count };
      }
      return { success: true, data: (data ?? []) as SecurityEventRecord[], count };
    } catch (error: any) {
      return { success: false, message: error?.message || 'Failed to load security events.' };
    }
  },

  async getSecurityAlertsPage(
    limit = 25,
    offset = 0,
    options?: {
      status?: SecurityAlertStatus;
      severity?: SecurityEventSeverity;
    }
  ): Promise<ApiResponse<SecurityAlertRecord[]> & { count?: number | null }> {
    try {
      let query = supabase
        .from('monitoring_alerts')
        .select('*', { count: 'exact' })
        .like('source', 'security.%')
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (options?.status) query = query.eq('status', options.status);
      if (options?.severity) query = query.eq('severity', options.severity);

      const { data, error, count } = await query;
      if (error) {
        return { success: false, message: error.message, count };
      }
      return { success: true, data: (data ?? []) as SecurityAlertRecord[], count };
    } catch (error: any) {
      return { success: false, message: error?.message || 'Failed to load security alerts.' };
    }
  },

  async getRecentSecurityAlertRuns(limit = 8): Promise<ApiResponse<SecurityAlertRunRecord[]>> {
    try {
      const { data, error } = await supabase
        .from('monitoring_alert_runs')
        .select('*')
        .eq('detector_name', 'super-admin-security-detector')
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) {
        return { success: false, message: error.message };
      }
      return { success: true, data: (data ?? []) as SecurityAlertRunRecord[] };
    } catch (error: any) {
      return { success: false, message: error?.message || 'Failed to load security detector runs.' };
    }
  },

  async acknowledgeAlert(id: string): Promise<ApiResponse<null>> {
    try {
      const { data: auth } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('monitoring_alerts')
        .update({
          status: 'acknowledged',
          acknowledged_by: auth.user?.id ?? null,
          acknowledged_at: new Date().toISOString(),
        })
        .eq('id', id);
      if (error) {
        return { success: false, message: error.message };
      }
      return { success: true, data: null };
    } catch (error: any) {
      return { success: false, message: error?.message || 'Failed to acknowledge security alert.' };
    }
  },

  async resolveAlert(id: string): Promise<ApiResponse<null>> {
    try {
      const { data: auth } = await supabase.auth.getUser();
      const nowIso = new Date().toISOString();
      const { error } = await supabase
        .from('monitoring_alerts')
        .update({
          status: 'resolved',
          resolved_by: auth.user?.id ?? null,
          resolved_at: nowIso,
          acknowledged_by: auth.user?.id ?? null,
          acknowledged_at: nowIso,
        })
        .eq('id', id);
      if (error) {
        return { success: false, message: error.message };
      }
      return { success: true, data: null };
    } catch (error: any) {
      return { success: false, message: error?.message || 'Failed to resolve security alert.' };
    }
  },
};

export const __securityMonitorTestUtils = {
  isSecurityAlertSource,
  securityErrorToMetadata,
};
